// Subscription primitives — the single source of truth for "what plan is this subscription, and
// does it qualify" (Stripe + RevenueCat webhooks, BYO Payments and custom-domain gates). Folded in
// from the former @folio/subscription package (#2667). Feature gates build separate eligibility
// checks on top of this; keeping the fragile derivation here stops them drifting into two copies.
//
// The stored `Subscription.interval` column is NOT reliable and must never be the sole
// signal: prod carries `Atelier_Monthly` with interval `yearly` (inverted), the real
// yearly `standard_yearly` with interval `month` (inverted), and inconsistent spelling
// (`year` vs `yearly`, `month` vs `monthly`) for the same plan. The tier NICKNAME encodes
// the period, so it is the authority; the column is a last-resort fallback only.

export type BillingInterval = "weekly" | "monthly" | "semiannual" | "yearly";

export type NormalizedSubscriptionTier = "free" | "basic" | "standard" | "premium";

export type SubscriptionProvider = "stripe" | "revenuecat";

// Tier nickname (Stripe price nickname or RC-derived tier string) -> normalized coarse tier.
// Keys are a hardcoded contract with historical Stripe/RC identifiers; do not rename them.
export const SUBSCRIPTION_TIERS: Record<string, NormalizedSubscriptionTier> = {
  free: "free",
  basic: "basic",
  standard: "standard",
  premium: "premium",
  Solo_Weekly: "basic",
  Solo_Monthly: "basic",
  Solo_Yearly: "basic",
  Solo_monthly_new: "basic",
  Pro_Monthly: "standard",
  Pro_Yearly: "standard",
  Max_Monthly: "premium",
  Max_Semiannual: "premium",
  Max_Yearly: "premium",
  Atelier_Monthly: "basic",
  basic_yearly_15042025: "basic",
  basic_monthly_15052025: "basic",
  basic_monthly_08_12_2025: "basic",
  basic_yearly_08_12_2025: "basic",
  basic_monthly_08_17_2026: "basic",
  basic_yearly_08_17_2026: "basic",
  "Standard Yearly": "standard",
  "Standard Monthly": "standard",
  standard_yearly: "standard",
  standard_yearly_15042025: "standard",
  standart_monthly_15042025: "standard",
  standard_monthly_08_12_2025: "standard",
  standard_yearly_08_12_2025: "standard",
  standard_monthly_08_17_2026: "standard",
  standard_yearly_08_17_2026: "standard",
  premium_monthly_15042025: "premium",
  premium_yearly_15042025: "premium",
  "Premium monthly": "premium",
  "Premium Yearly": "premium",
  premium_monthly_08_12_2025: "premium",
  premium_yearly_08_12_2025: "premium",
  premium_monthly_08_17_2026: "premium",
  premium_yearly_08_17_2026: "premium",
  premium_yearly_discounted: "premium",
};

/** Normalize a raw tier nickname to a coarse tier; unknown nicknames fall back to "free". */
export function normalizeSubscriptionTier(
  rawTier: string | null | undefined,
): NormalizedSubscriptionTier {
  // Object.hasOwn, not `?? "free"` on a bracket read: a bare-object lookup of an inherited key
  // ("toString", "valueOf") would otherwise return the prototype's function instead of "free".
  return rawTier && Object.hasOwn(SUBSCRIPTION_TIERS, rawTier)
    ? SUBSCRIPTION_TIERS[rawTier]
    : "free";
}

function intervalFromToken(raw: string | null | undefined): BillingInterval | null {
  if (!raw) return null;
  const lowered = raw.toLowerCase();
  if (lowered.includes("semiannual")) return "semiannual";
  if (lowered.includes("year")) return "yearly";
  if (lowered.includes("month")) return "monthly";
  if (lowered.includes("week")) return "weekly";
  return null;
}

/**
 * Derive the true billing interval of a Stripe subscription. The tier nickname is the
 * authority; the stored `interval` column is only consulted when the nickname carries no
 * period token (e.g. bare `basic`). Returns null when neither yields a period.
 */
export function resolveStripeInterval(input: {
  tier: string | null | undefined;
  interval?: string | null;
}): BillingInterval | null {
  return intervalFromToken(input.tier) ?? intervalFromToken(input.interval);
}

/**
 * Resolve a Stripe subscription's stored fields to its normalized `{ tier, interval }`.
 * Returns null when the tier nickname is unrecognized (never emit a garbage tier) or the
 * interval cannot be derived. Mirrors `resolveRcProduct` on the RevenueCat side.
 */
export function resolveStripeProduct(input: {
  tier: string | null | undefined;
  interval?: string | null;
}): { tier: NormalizedSubscriptionTier; interval: BillingInterval } | null {
  const rawTier = input.tier ?? "";
  if (!Object.hasOwn(SUBSCRIPTION_TIERS, rawTier)) return null;
  const interval = resolveStripeInterval(input);
  if (!interval) return null;
  return { tier: SUBSCRIPTION_TIERS[rawTier], interval };
}

// The billing-period tokens we accept as a valid stored `interval` value. Stripe writes raw
// `month`/`year`; RevenueCat writes `weekly`/`monthly`/`semiannual`/`yearly`. Anything OUTSIDE this set (a tier
// name like `Solo_Yearly` written into the column, a raw store id) is garbage and must never be
// trusted as a period signal — only a recognized period nickname (in `SUBSCRIPTION_TIERS`) may be.
export const RECOGNIZED_INTERVAL_TOKENS: ReadonlySet<string> = new Set([
  "week",
  "weekly",
  "month",
  "monthly",
  "semiannual",
  "year",
  "yearly",
]);

/**
 * The interval to STORE on a subscription row, canonicalized so the credit-refill cadence
 * (derived from `Subscription.interval`) can never disagree with the plan. The tier NICKNAME is the
 * authority — the stored column is unreliable (inverted / tier-name garbage / mixed spelling), exactly
 * as the module header warns — so a mis-supplied `weekly` on a monthly plan is corrected, not persisted.
 * Output vocabulary is always `weekly | monthly | semiannual | yearly`.
 *
 * The authority is the tier only when it is a RECOGNIZED nickname. Two garbage-in traps are closed by
 * that gate: a raw (legacy-inverted) RC store id passed as the tier must never have its period inferred
 * from the id name (`.weekly` is the MONTHLY plan), and a tier name written into the interval column is
 * not a period signal either. Both fall through to the safe monthly default rather than an accidental weekly.
 *
 * Resolution order:
 *  1. KNOWN tier nickname carries a period token → that wins (Solo_Monthly → monthly, even vs interval `weekly`).
 *  2. KNOWN tier that is `free` → `monthly` (free's stored interval is untrusted; cadence-30, owner decision).
 *  3. otherwise trust the stored interval only if it is a RECOGNIZED token (`month` → monthly).
 *  4. nothing trustworthy → `monthly` (never emit empty or an accidental weekly; monthly is cadence-30 safe).
 */
export function canonicalSubscriptionInterval(input: {
  tier: string | null | undefined;
  interval?: string | null;
}): BillingInterval {
  const rawTier = input.tier ?? "";
  if (Object.hasOwn(SUBSCRIPTION_TIERS, rawTier)) {
    if (SUBSCRIPTION_TIERS[rawTier] === "free") return "monthly";
    const fromTier = intervalFromToken(rawTier);
    if (fromTier) return fromTier;
  }
  const interval = (input.interval ?? "").toLowerCase();
  const fromInterval = intervalFromToken(interval);
  if (fromInterval && RECOGNIZED_INTERVAL_TOKENS.has(interval)) return fromInterval;
  return "monthly";
}

// "*" matches any value for that field; a concrete value matches only itself. A plan set is
// a list of such clauses — a subscription is eligible when it matches ANY clause. Features
// declare their own set (e.g. any tier yearly, or premium yearly); extending eligibility is
// adding a clause, never a hardcoded provider/interval constant at the call site.
export type PlanField<T extends string> = T | "*";

export type EligiblePlan = {
  provider?: PlanField<SubscriptionProvider>;
  tier: PlanField<NormalizedSubscriptionTier>;
  interval: PlanField<BillingInterval>;
};

export type EligiblePlanSet = readonly EligiblePlan[];

export type EligibilitySubject = {
  provider: SubscriptionProvider;
  tier: NormalizedSubscriptionTier;
  interval: BillingInterval;
};

function fieldMatches<T extends string>(clause: PlanField<T> | undefined, value: T): boolean {
  return clause === undefined || clause === "*" || clause === value;
}

/** True when the subject matches at least one clause of the eligible-plan set. */
export function matchesEligiblePlanSet(subject: EligibilitySubject, set: EligiblePlanSet): boolean {
  return set.some(
    (plan) =>
      fieldMatches(plan.provider, subject.provider) &&
      fieldMatches(plan.tier, subject.tier) &&
      fieldMatches(plan.interval, subject.interval),
  );
}
